import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { getDefaultOpenPrizeId } from '../utils';
import PrizeDoor from './PrizeDoor';
import prizePropType from './prizePropType';

// Matches the door's width transition, so scrolling measures the door once it has grown.
const OPEN_TRANSITION_MS = 300;

/**
 * One door per prize day. Doors stack as slim strips on mobile and stand side by
 * side from `lg`; the open door grows to show the prize. When the doors no longer
 * fit their minimum widths, the row scrolls sideways.
 */
export default function PrizeDoors({
  prizes,
  getButtonText,
  onParticipate,
  onDetails
}) {
  const [selectedId, setSelectedId] = useState(null);
  const defaultOpenId = useMemo(() => getDefaultOpenPrizeId(prizes), [prizes]);
  const openId = selectedId ?? defaultOpenId;
  const rowRef = useRef(null);

  // Keep the open door in view when the row scrolls; a no-op when everything fits.
  useEffect(() => {
    const row = rowRef.current;
    const index = prizes.findIndex(prize => prize.id === openId);
    if (!row || index === -1) return undefined;

    const timeout = setTimeout(
      () => {
        if (row.scrollWidth <= row.clientWidth) return;
        const door = row.children[index];
        const padding = parseFloat(getComputedStyle(row).paddingLeft);
        const start = door.offsetLeft - padding;
        const end =
          door.offsetLeft + door.offsetWidth + padding - row.clientWidth;
        if (start < row.scrollLeft) {
          row.scrollTo({ left: start, behavior: 'smooth' });
        } else if (end > row.scrollLeft) {
          row.scrollTo({ left: end, behavior: 'smooth' });
        }
      },
      selectedId ? OPEN_TRANSITION_MS : 0
    );
    return () => clearTimeout(timeout);
  }, [openId, prizes, selectedId]);

  if (!prizes.length) return null;

  return (
    // The row's padding, offset by negative margins, leaves room for the open door's
    // glow, which the scroll container would otherwise clip. The side padding is capped
    // by the page margin at each breakpoint. flow-root keeps the margins from collapsing
    // into the spacing around the row.
    <div className="lg:flow-root">
      <div
        ref={rowRef}
        className="relative flex flex-col gap-2 lg:-mx-3 lg:-my-12 lg:h-[656px] lg:snap-x lg:scroll-px-3 lg:flex-row lg:gap-2.5 lg:overflow-x-auto lg:px-3 lg:py-12 xl:-mx-8 xl:scroll-px-8 xl:px-8"
      >
        {prizes.map(prize => (
          <PrizeDoor
            key={prize.id}
            prize={prize}
            isOpen={prize.id === openId}
            getButtonText={getButtonText}
            onOpen={() => setSelectedId(prize.id)}
            onParticipate={onParticipate}
            onDetails={onDetails}
          />
        ))}
      </div>
    </div>
  );
}

PrizeDoors.propTypes = {
  prizes: PropTypes.arrayOf(prizePropType).isRequired,
  getButtonText: PropTypes.func.isRequired,
  onParticipate: PropTypes.func.isRequired,
  onDetails: PropTypes.func.isRequired
};
